import { useState } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, BarChart3, Calculator, FileText, Image, LayoutGrid, LogOut, Mail, Menu, Newspaper, Package, Plug, Receipt, Settings, ShoppingBag, Tag, Users, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "../lib/api";
import { Daisy, DaisyBadge } from "../components/Daisy";
import Dashboard from "./Dashboard";
import Products from "./Products";
import Newsletter from "./Newsletter";
import Accounting from "./Accounting";
import { Categories, Coupons, HeroSlides } from "./Catalog";
import { Customers, Invoices, Orders } from "./OrdersAdmin";
import { Communication, Integrations, InvoiceDesigner, SiteSettings } from "./SettingsAdmin";

const MENU = [
  ["", "Přehled", BarChart3], ["objednavky", "Objednávky", ShoppingBag], ["produkty", "Produkty", Package], ["kategorie", "Kategorie", LayoutGrid],
  ["hero", "Hero slider", Image], ["slevy", "Slevy a poukazy", Tag], ["newsletter", "Newsletter", Newspaper], ["zakaznici", "Zákazníci", Users], ["faktury", "Faktury", Receipt], ["ucetnictvi", "Účetnictví", Calculator], ["faktura", "Návrh faktury", FileText],
  ["obsah", "Obsah webu", Settings], ["platby-emaily", "Platby a e-maily", Plug], ["komunikace", "Odeslané e-maily", Mail],
];

const BackToWeb = ({ className = "" }) => (
  <a href="/" className={`flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm text-white/80 hover:bg-white/10 ${className}`} data-testid="admin-back-to-web"><ArrowLeft className="h-4 w-4" /> Zpět na web</a>
);

const Login = ({ onIn }) => {
  const [f, setF] = useState({ username: "", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await adminApi.post("/login", f);
      localStorage.setItem("hm_admin", data.token);
      onIn();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="relative grid min-h-screen place-items-center bg-[#E9EFE0] p-4">
      <a href="/" className="btn-ghost absolute left-4 top-4 bg-white" data-testid="admin-login-back"><ArrowLeft className="h-4 w-4" /> Zpět na web</a>
      <motion.form initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} onSubmit={submit} className="card-soft w-full max-w-sm space-y-4 p-8 text-center" data-testid="admin-login-form">
        <span className="mx-auto grid h-24 w-24 place-items-center rounded-full bg-[#A3AC86]"><Daisy size={70} animatePetals /></span>
        <h1 className="h-display text-3xl">Administrace</h1>
        <input className="input-soft" placeholder="Uživatelské jméno" autoCapitalize="none" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} data-testid="admin-username-input" />
        <input className="input-soft" type="password" placeholder="Heslo" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-password-input" />
        <button className="btn-sage w-full" data-testid="admin-login-btn">Přihlásit</button>
      </motion.form>
    </div>
  );
};

const SideNav = ({ onNavigate, logout }) => (
  <div className="flex h-full flex-col">
    <div className="flex items-center gap-3 p-6">
      <DaisyBadge size={44} />
      <div className="leading-tight"><p className="font-serif text-lg font-black italic">hravá mateřinka</p><p className="text-xs text-white/60">CRM & e-shop</p></div>
    </div>
    <nav className="flex-1 space-y-1 overflow-y-auto px-4">
      {MENU.map(([to, l, I]) => (
        <NavLink key={to} to={`/admin/${to}`} end onClick={onNavigate} data-testid={`admin-tab-${to || "dashboard"}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-2xl px-4 py-2.5 text-sm transition-colors ${isActive ? "bg-[#A3AC86] text-white" : "text-white/75 hover:bg-white/10"}`}>
          <I className="h-4 w-4" /> {l}
        </NavLink>
      ))}
    </nav>
    <div className="space-y-1 border-t border-white/10 p-4">
      <BackToWeb />
      <button onClick={logout} className="flex w-full items-center gap-3 rounded-2xl px-4 py-2.5 text-sm text-white/80 hover:bg-white/10" data-testid="admin-logout-btn"><LogOut className="h-4 w-4" /> Odhlásit</button>
    </div>
  </div>
);

export default function AdminApp() {
  const [authed, setAuthed] = useState(!!localStorage.getItem("hm_admin"));
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  if (!authed) return <Login onIn={() => setAuthed(true)} />;
  const logout = () => {
    localStorage.removeItem("hm_admin");
    setAuthed(false);
    nav("/admin");
  };
  return (
    <div className="min-h-screen bg-[#F3EFE5] lg:flex" data-testid="admin-app">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 bg-[#2D3426] text-white lg:block"><SideNav logout={logout} /></aside>
      <header className="sticky top-0 z-30 flex items-center gap-3 bg-[#2D3426] px-4 py-3 text-white lg:hidden">
        <button onClick={() => setOpen(true)} className="grid h-10 w-10 place-items-center rounded-full bg-white/10" data-testid="admin-menu-btn" aria-label="Menu"><Menu /></button>
        <p className="flex-1 font-serif text-lg font-black italic">hravá mateřinka</p>
        <a href="/" className="flex items-center gap-1 rounded-full bg-white/10 px-3 py-2 text-xs" data-testid="admin-mobile-back"><ArrowLeft className="h-4 w-4" /> Web</a>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 bg-black/50 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
            <motion.aside initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "spring", damping: 26 }} onClick={(e) => e.stopPropagation()}
              className="relative h-full w-72 bg-[#2D3426] text-white" data-testid="admin-mobile-menu">
              <button onClick={() => setOpen(false)} className="absolute right-3 top-3 rounded-full p-2 hover:bg-white/10" data-testid="admin-menu-close"><X className="h-5 w-5" /></button>
              <SideNav onNavigate={() => setOpen(false)} logout={logout} />
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
      <main className="min-w-0 flex-1 p-4 sm:p-8">
        <Routes>
          <Route path="/admin" element={<Dashboard />} />
          <Route path="/admin/objednavky" element={<Orders />} />
          <Route path="/admin/produkty" element={<Products />} />
          <Route path="/admin/kategorie" element={<Categories />} />
          <Route path="/admin/hero" element={<HeroSlides />} />
          <Route path="/admin/slevy" element={<Coupons />} />
          <Route path="/admin/newsletter" element={<Newsletter />} />
          <Route path="/admin/ucetnictvi" element={<Accounting />} />
          <Route path="/admin/zakaznici" element={<Customers />} />
          <Route path="/admin/faktury" element={<Invoices />} />
          <Route path="/admin/platby-emaily" element={<Integrations />} />
          <Route path="/admin/faktura" element={<InvoiceDesigner />} />
          <Route path="/admin/obsah" element={<SiteSettings />} />
          <Route path="/admin/komunikace" element={<Communication />} />
          <Route path="*" element={<Dashboard />} />
        </Routes>
      </main>
    </div>
  );
}
